
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { FormField, FormItem, FormLabel, FormControl, FormDescription, Form } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { UseFormReturn } from 'react-hook-form';

interface NewsletterReferralSectionProps {
  form: UseFormReturn<any>;
}

export const NewsletterReferralSection: React.FC<NewsletterReferralSectionProps> = ({ form }) => {
  const emailProviders = [
    { value: 'mailchimp', label: 'Mailchimp' },
    { value: 'klaviyo', label: 'Klaviyo' },
    { value: 'convertkit', label: 'ConvertKit' },
    { value: 'campaign-monitor', label: 'Campaign Monitor' },
    { value: 'hubspot', label: 'HubSpot' },
    { value: 'sendgrid', label: 'SendGrid' },
    { value: 'activecampaign', label: 'ActiveCampaign' },
    { value: 'marketo', label: 'Marketo' },
    { value: 'other', label: 'Other' }
  ];

  const referralCountTypes = [
    { value: 'signup', label: 'On Signup' },
    { value: 'confirmed', label: 'After Email Confirmation' },
    { value: 'paid', label: 'After First Payment' }
  ];

  // Initialize newsletterSettings if it doesn't exist
  React.useEffect(() => {
    if (!form.getValues().newsletterSettings) {
      form.setValue('newsletterSettings', {
        emailProvider: '',
        confirmationTemplateId: '',
        welcomeTemplateId: '',
        customReferralMessage: '',
        trackingParameter: 'ref',
        showReferralLinkInEmails: true,
        enableRewardHistory: true,
        referralCountType: 'confirmed'
      });
    }
  }, [form]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Newsletter Integration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Form {...form}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="newsletterSettings.emailProvider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Provider</FormLabel>
                  <Select 
                    value={field.value} 
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select email provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {emailProviders.map(provider => (
                        <SelectItem key={provider.value} value={provider.value}>
                          {provider.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    The email marketing platform you use for your newsletter
                  </FormDescription>
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="newsletterSettings.confirmationTemplateId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirmation Template ID</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. template_12345" />
                    </FormControl>
                    <FormDescription>
                      Template for confirmation emails
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newsletterSettings.welcomeTemplateId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Welcome Template ID</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. template_67890" />
                    </FormControl>
                    <FormDescription>
                      Template for welcome emails
                    </FormDescription>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="newsletterSettings.customReferralMessage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referral Message</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Refer friends and earn rewards!" />
                  </FormControl>
                  <FormDescription>
                    Message shown in emails to encourage referrals
                  </FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="newsletterSettings.trackingParameter"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tracking Parameter</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="ref" />
                  </FormControl>
                  <FormDescription>
                    URL parameter used to track referrals (e.g., ?ref=user123)
                  </FormDescription>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="newsletterSettings.referralCountType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Count Referrals</FormLabel>
                  <Select 
                    value={field.value} 
                    onValueChange={field.onChange}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select when to count referrals" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {referralCountTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    When to count a referral as successful
                  </FormDescription>
                </FormItem>
              )}
            />

            <div className="space-y-4 pt-4">
              <FormField
                control={form.control}
                name="newsletterSettings.showReferralLinkInEmails"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-md p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Show Referral Link in Emails
                      </FormLabel>
                      <FormDescription>
                        Include referral link in newsletter emails
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="newsletterSettings.enableRewardHistory"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between border rounded-md p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        Enable Reward History
                      </FormLabel>
                      <FormDescription>
                        Let users see their referral rewards history
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            {form.watch('newsletterSettings.emailProvider') && (
              <FormField
                control={form.control}
                name="newsletterSettings.integrationKey"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Integration Key</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Your API key" type="password" />
                    </FormControl>
                    <FormDescription>
                      API key for connecting to your email provider
                    </FormDescription>
                  </FormItem>
                )}
              />
            )}
          </div>
        </Form>
      </CardContent>
    </Card>
  );
};

export default NewsletterReferralSection;
